// TodoList.tsx
import React, { useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  SimpleGrid,
  Flex,
  useToast,
  FormControl,
  Select,
  Text,
  GridItem,
  VStack,
  Input,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Divider,
} from "@chakra-ui/react";
import TextareaAutosize from "react-textarea-autosize";
import { useDispatch, useSelector } from "react-redux";
import {
  getData,
  getDataById,
  postData,
} from "../../../redux/actions/common.action";
import { useNavigate } from "react-router-dom";
import TodoDemo from "./TodoDemo";
import { useForm, useWatch } from "react-hook-form";
import {
  Home,
  CheckSquare,
  ChevronRight,
  Plus,
  Calendar,
  User,
  Building2,
  MapPin,
  FileText,
} from "lucide-react";
import {
  isLockedRegularTeamUser,
  isTeamManagerTeamUser,
  normalizeId,
} from "../../../utils/teamUserForm";

interface TodoItem {
  todos: any;
  branches: any;
  cities: any;
  users: any;
  signinuser: any;
  user: any;
  loading: boolean;
}

interface City {
  _id: string;
  cityName: string;
  branchId: {
    _id: string;
    BranchName: string;
  };
}

interface TodoFormData {
  assignId: string;
  todoText: string;
  branchId: string;
  cityId: string;
  dueDate: Date;
}

const TodoList: React.FC = () => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TodoFormData>();

  const { todos, branches, cities, users, signinuser, user, loading } =
    useSelector((state: { common: TodoItem }) => state.common);

  const navigate = useNavigate();
  const toast = useToast();
  const dispatch: any = useDispatch();
  const endpoint = "todo";

  const selectedBranchId = useWatch({ control, name: "branchId" });
  const selectedCityId = useWatch({ control, name: "cityId" });

  const lockedRegularTeam = isLockedRegularTeamUser(signinuser, user);
  const isAdmin =
    signinuser?.UserType === 1 || signinuser?.UserType === 2;

  const reapplyLockedTeamDefaults = useCallback(() => {
    if (!isLockedRegularTeamUser(signinuser, user)) return;
    const branchId = normalizeId(user?.Branch);
    const b = branches?.find((x: any) => normalizeId(x._id) === branchId);
    if (b) setValue("branchId", normalizeId(b._id));
    const cityIdVal = normalizeId(user?.City ?? signinuser?.City);
    const c = cities?.find((x: City) => normalizeId(x._id) === cityIdVal);
    if (c) setValue("cityId", normalizeId(c._id));
    else if (cityIdVal) setValue("cityId", cityIdVal);
    const selfId = normalizeId(signinuser?.userId);
    if (selfId) setValue("assignId", selfId);
  }, [signinuser, user, branches, cities, setValue]);

  useEffect(() => {
    if (signinuser?.UserType === 3 && !isTeamManagerTeamUser(signinuser, user))
      return;
    setValue("cityId", "");
    setValue("assignId", "");
  }, [selectedBranchId, signinuser, user, setValue]);

  useEffect(() => {
    if (signinuser?.UserType === 3 && !isTeamManagerTeamUser(signinuser, user))
      return;
    setValue("assignId", "");
  }, [selectedCityId, signinuser, user, setValue]);

  useEffect(() => {
    if (!todos?.length) dispatch(getData("todo"));
    if (!branches?.length) dispatch(getData("branch"));
    dispatch(getData("user"));
    if (!cities?.length) dispatch(getData("city"));
  }, []);

  useEffect(() => {
    if (!signinuser?.userId) return;
    if (!user || normalizeId(user._id) !== normalizeId(signinuser.userId)) {
      dispatch(getDataById(signinuser?.userId, "user"));
    }
  }, [signinuser?.userId, user, dispatch]);

  useEffect(() => {
    if (!signinuser?.userId) return;

    const locked = isLockedRegularTeamUser(signinuser, user);
    const type3 = signinuser.UserType === 3;

    if (type3 && user?.Branch) {
      const branchId = normalizeId(user.Branch);
      const b = branches?.find((x: any) => normalizeId(x._id) === branchId);
      if (b) setValue("branchId", normalizeId(b._id));
    }

    if (locked) {
      const selfId = normalizeId(signinuser.userId);
      const cityRaw = user?.City ?? signinuser?.City;
      const cityIdVal = normalizeId(cityRaw);

      const cityDoc = cities?.find(
        (c: any) => normalizeId(c._id) === cityIdVal,
      );
      if (cityDoc) {
        setValue("cityId", normalizeId(cityDoc._id));
        const brFromCity = normalizeId(
          cityDoc.branchId?._id ?? cityDoc.branchId,
        );
        if (brFromCity) {
          const bHit = branches?.find(
            (x: any) => normalizeId(x._id) === brFromCity,
          );
          if (bHit) setValue("branchId", normalizeId(bHit._id));
        }
      } else if (cityIdVal) {
        setValue("cityId", cityIdVal);
      }

      if (selfId) setValue("assignId", selfId);
    }
  }, [signinuser, user, branches, cities, setValue]);

  const branchoptions = branches?.map((branch: any) => (
    <option key={branch._id} value={normalizeId(branch._id)}>
      {branch.BranchName}
    </option>
  ));

  const cityoptions = useMemo(() => {
    return cities
      ?.filter((c: any) => {
        const cb = normalizeId(c?.branchId?._id ?? c?.branchId);
        return cb === normalizeId(selectedBranchId);
      })
      .map((c: any) => (
        <option key={c._id} value={normalizeId(c._id)}>
          {c.cityName}
        </option>
      ));
  }, [cities, selectedBranchId]);

  const useroptions = useMemo(() => {
    const selBr = normalizeId(selectedBranchId);
    const selCt = normalizeId(selectedCityId);
    const selfId = normalizeId(signinuser?.userId);

    const filtered = (users || []).filter((u: any) => {
      if (u?.active === false || u?.isActive === false) return false;

      if (!isAdmin && u.UserType !== 3) return false;
      if (isAdmin && u.UserType !== 2 && u.UserType !== 3) return false;

      const uBr = normalizeId(u?.Branch?._id ?? u.Branch);
      const uCt = normalizeId(u?.City?._id ?? u.City);

      if (!selBr) return false;
      if (uBr !== selBr) return false;
      if (selCt) return uCt === selCt;
      return isAdmin;
    });

    const needSelfOption =
      lockedRegularTeam &&
      selfId &&
      !filtered.some((u: any) => normalizeId(u._id) === selfId);

    const selfLabel = signinuser?.Name || user?.Name || "You";

    return [
      ...(needSelfOption
        ? [
            <option key={`todo-self-${selfId}`} value={selfId}>
              {selfLabel}
            </option>,
          ]
        : []),
      ...filtered.map((u: any) => {
        const id = normalizeId(u._id);
        const isSelf = id === selfId;
        return (
          <option key={id} value={id}>
            {isSelf ? `${u.Name} (You)` : u.Name}
          </option>
        );
      }),
    ];
  }, [
    users,
    selectedBranchId,
    selectedCityId,
    signinuser,
    user,
    isAdmin,
    lockedRegularTeam,
  ]);

  const citySelectDisabled =
    lockedRegularTeam || !selectedBranchId;

  const assignSelectDisabled =
    lockedRegularTeam ||
    !selectedBranchId ||
    (!isAdmin && !selectedCityId);

  const onsubmit = (data: any) => {
    const payload = {
      note: data.todoText,
      assignId: data.assignId,
      dueDate: data.dueDate,
    };
    dispatch(postData(payload, endpoint, navigate, toast)).then(() => {
      dispatch(getData(endpoint));
      setValue("todoText", "");
      setValue("dueDate", "" as any);
      setValue("branchId", "");
      setValue("cityId", "");
      setValue("assignId", "");
      reapplyLockedTeamDefaults();
    });
  };

  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box p="8px" bg="gray.50" minH="100vh">
      <style>
        {`
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in-left {
            animation: slideInLeft 0.4s ease-out;
          }
        `}
      </style>

      {/* Modern Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={4}
        className="slide-in-left"
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <VStack align="start" spacing={2}>
            <Breadcrumb
              spacing={2}
              separator={<ChevronRight size={12} color="#9CA3AF" />}
              fontSize="xs"
              color="gray.600"
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBack}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Dashboard</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <CheckSquare size={12} />
                  <Text>Task Management</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </VStack>

          <HStack spacing={4}>
            <VStack align="end" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                Task Management
              </Text>
              <Text fontSize="xs" color="gray.500">
                Create and manage tasks
              </Text>
            </VStack>
            <Box
              bgGradient="linear(to-br, blue.400, blue.600)"
              p={3}
              borderRadius="xl"
              boxShadow="md"
            >
              <CheckSquare size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      <form onSubmit={handleSubmit(onsubmit)}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={3}>
          {/* Left Panel - Form */}
          <GridItem>
            <Box
              bg="white"
              borderRadius="xl"
              boxShadow="sm"
              border="1px"
              borderColor="gray.200"
              p={6}
            >
              <Flex align="center" gap={2} mb={4}>
                <Box bg="blue.50" p={2} borderRadius="lg">
                  <Plus size={20} color="#3B82F6" />
                </Box>
                <Text fontSize="md" fontWeight="bold" color="gray.800">
                  Create New Task
                </Text>
              </Flex>
              <Divider mb={6} />

              <VStack spacing={4} align="stretch">
                {/* Branch Select */}
                <FormControl isInvalid={!!errors?.branchId}>
                  <HStack spacing={2} mb={2}>
                    <Building2 size={16} color="#6B7280" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Branch *
                    </Text>
                  </HStack>
                  <Select
                    size="sm"
                    placeholder={
                      loading ? "Loading Branch..." : "Select Branch"
                    }
                    isDisabled={signinuser?.UserType === 3}
                    borderColor={errors?.branchId ? "red.500" : "gray.300"}
                    borderRadius="md"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                    {...register("branchId", {
                      required: "Branch is required.",
                    })}
                  >
                    {branchoptions}
                  </Select>
                  {errors.branchId && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {errors.branchId.message}
                    </Text>
                  )}
                </FormControl>

                {/* City Select */}
                <FormControl>
                  <HStack spacing={2} mb={2}>
                    <MapPin size={16} color="#6B7280" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      City
                    </Text>
                  </HStack>
                  <Select
                    size="sm"
                    placeholder={
                      selectedBranchId
                        ? "Select City"
                        : "Please Select Branch First"
                    }
                    isDisabled={citySelectDisabled}
                    borderColor="gray.300"
                    borderRadius="md"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                    {...register("cityId")}
                  >
                    {cityoptions}
                  </Select>
                </FormControl>

                {/* Assign To */}
                <FormControl isInvalid={!!errors?.assignId}>
                  <HStack spacing={2} mb={2}>
                    <User size={16} color="#6B7280" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Assign To *
                    </Text>
                  </HStack>
                  <Select
                    size="sm"
                    placeholder={
                      !selectedBranchId
                        ? "Please Select Branch First"
                        : !isAdmin && !selectedCityId
                          ? "Please Select City First"
                          : "Select Assigned To"
                    }
                    isDisabled={assignSelectDisabled}
                    borderColor={errors?.assignId ? "red.500" : "gray.300"}
                    borderRadius="md"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                    {...register("assignId", {
                      required: "Assigned To is required.",
                    })}
                  >
                    {useroptions}
                  </Select>
                  {errors.assignId && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {errors.assignId.message}
                    </Text>
                  )}
                </FormControl>

                {/* Due Date */}
                <FormControl isInvalid={!!errors?.dueDate}>
                  <HStack spacing={2} mb={2}>
                    <Calendar size={16} color="#6B7280" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Due Date *
                    </Text>
                  </HStack>
                  <Input
                    type="datetime-local"
                    size="sm"
                    borderColor={errors?.dueDate ? "red.500" : "gray.300"}
                    borderRadius="md"
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                    {...register("dueDate", {
                      required: "Due Date is required.",
                    })}
                  />
                  {errors.dueDate && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {errors.dueDate.message}
                    </Text>
                  )}
                </FormControl>

                {/* Task Description */}
                <FormControl isInvalid={!!errors?.todoText}>
                  <HStack spacing={2} mb={2}>
                    <FileText size={16} color="#6B7280" />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Task Description *
                    </Text>
                  </HStack>
                  <TextareaAutosize
                    minRows={4}
                    maxRows={6}
                    placeholder="Enter task description..."
                    style={{
                      width: "100%",
                      padding: "8px",
                      fontSize: "0.875rem",
                      borderRadius: "6px",
                      border: `1px solid ${
                        errors?.todoText ? "#FC8181" : "#CBD5E0"
                      }`,
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                    {...register("todoText", {
                      required: "Task description is required.",
                    })}
                  />
                  {errors.todoText && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      {errors.todoText.message}
                    </Text>
                  )}
                </FormControl>
              </VStack>

              <Flex justify="flex-end" mt={6}>
                <Button
                  type="submit"
                  leftIcon={<Plus size={16} />}
                  size="md"
                  fontWeight="600"
                  color="white"
                  bgGradient="linear(to-r, blue.500, purple.500)"
                  _hover={{
                    bgGradient: "linear(to-r, blue.600, purple.600)",
                    transform: "translateY(-2px) scale(1.02)",
                    boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
                  }}
                  _active={{
                    transform: "translateY(0) scale(0.98)",
                    boxShadow: "0 2px 8px rgba(99, 102, 241, 0.2)",
                  }}
                  transition="all 0.25s ease-in-out"
                  borderRadius="full"
                  px={6}
                  py={2.5}
                  boxShadow="0 4px 14px rgba(99, 102, 241, 0.3)"
                  position="relative"
                  overflow="hidden"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-75%",
                    width: "50%",
                    height: "100%",
                    bg: "whiteAlpha.300",
                    transform: "skewX(-20deg)",
                    transition: "left 0.5s ease",
                  }}
                >
                  Add Task
                </Button>
              </Flex>
            </Box>
          </GridItem>

          {/* Right Panel - Todo List */}
          <GridItem>
            <Box
              bg="white"
              borderRadius="xl"
              boxShadow="sm"
              border="1px"
              borderColor="gray.200"
              p={6}
            >
              <TodoDemo />
            </Box>
          </GridItem>
        </SimpleGrid>
      </form>
    </Box>
  );
};

export default TodoList;
